import focusableSelectors from '../helpers/focusable-selectors';
import KEYCODE from '../helpers/keycodes';
import { isVisible } from '../helpers/helpers';
import { lockBodyScroll, unlockBodyScroll } from '../utilities/lock-body-scroll';

const modals = [];
let modalOpeners;

export class Modal {
    constructor(element, opener = null, settings = {}) {
        this.modal = element;
        this.modalContent = this.modal.querySelector('.jwc-modal-content');
        this.modalHeader = this.modal.querySelector('.jwc-modal-header');
        this.modalBody = this.modal.querySelector('.jwc-modal-body');
        this.adjustable = this.modal.hasAttribute('data-modal-adjustable');
        this.controllers = this.getModalControllers();
        this.showCallback = settings.showCallback || null;
        this.hideCallback = settings.hideCallback || null;

        this.modal.setAttribute('hidden', true);
        this.modal.setAttribute('role', 'dialog');
        this.modal.setAttribute('aria-modal', true);

        this.initialOpenState = this.modal.dataset.modalInitialOpenState;
        this.maxHeight = this.modal.dataset.modalMaxHeight;

        this.show = this.show.bind(this);
        this.hide = this.hide.bind(this);
        this.maintainFocus = this.maintainFocus.bind(this);
        this.keyDownHandler = this.keyDownHandler.bind(this);

        this.bodyCloser = this.modal.hasAttribute('data-body-closer');
        this.lockBodyScroll = this.modal.hasAttribute('data-lock-body-scroll') ? this.modal.dataset.lockBodyScroll : false;

        this.addControllerEvents();

        this.autoOpener = settings.autoOpen;
        this.autoOpenMaxCount = this.autoOpener ? this.modal.dataset.modalAutoOpen : null;
        this.opener = opener;

        this.sessionStorageClosedKey = `${this.modal.id}-closed`;

        if (this.autoOpener && !this.autoOpenMaxCount) {
            if (this.getModalClosedSession()) return;

            this.show();
        }

        if (this.autoOpener && this.autoOpenMaxCount) {
            this.autoOpenMaxCount = parseInt(this.autoOpenMaxCount);
            this.localStorageOpenCountKey = `${this.modal.id}-open-count`;
            this.autoOpenController();
        }
    }

    show(e) {
        if (!this.autoOpener && this.opener) {
            e.preventDefault();
        }

        if (this.showCallback && typeof this.showCallback === 'function') {
            this.showCallback(this.modal);
        }

        if (this.isShown) {
            return;
        }

        this.isShown = true;
        this.modal.removeAttribute('hidden');

        if (this.previouslyFocused) {
            this.previouslyFocused.classList.remove('target-open');
        }

        this.previouslyFocused = this.opener || document.activeElement;
        this.previouslyFocused.classList.add('target-open');

        this.setOpenProperties();

        if (!this.modal.hasAttribute('open')) {
            this.modal.setAttribute('open', '');
            this.modalContent.style.opacity = 0;

            // Settime out used to fix Safari modal content jumping from hidden to open
            setTimeout(() => {
                this.modalContent.style.opacity = 1;
                this.modal.dataset.modalState = 'opening';

                if (this.hasAnimation(this.modalContent)) {
                    this.modal.addEventListener(
                        'animationend',
                        () => {
                            this.modal.dataset.modalState = this.initialOpenState;
                        },
                        { once: true }
                    );
                } else {
                    this.modal.dataset.modalState = this.initialOpenState;
                }
            }, 1);
        }

        this.moveFocusIn();

        document.addEventListener('keydown', this.keyDownHandler);
        document.body.addEventListener('focus', this.maintainFocus, true);

        if (this.adjustable) {
            this.addPointerEvents();
        }

        if (this.lockBodyScroll) {
            lockBodyScroll(this.modal);
        }

        if (this.bodyCloser) {
            document.addEventListener('click', this.documentBodyClickHandler, true);
        }
    }

    hide = e => {
        this.isShown = false;
        this.modalBody.style.setProperty('--modal-body-height', `${this.modalBody.offsetHeight}px`);
        this.modal.dataset.modalState = 'closing';
        this.modal.removeAttribute('open');

        if (this.hasAnimation(this.modalContent)) {
            this.modal.addEventListener(
                'animationend',
                () => {
                    this.setClosedProperties();
                },
                { once: true }
            );
        } else {
            this.setClosedProperties();
        }

        document.removeEventListener('keydown', this._keyDownHandler);
        document.body.removeEventListener('focus', this.maintainFocus, true);

        if (this.adjustable) {
            this.removePointerEvents();
        }

        if (this.autoOpenMaxCount) {
            sessionStorage.setItem(this.sessionStorageClosedKey, true);
        }

        if (this.bodyCloser) {
            document.removeEventListener('click', this.documentBodyClickHandler, true);
        }

        if (this.previouslyFocused && this.previouslyFocused.focus) {
            this.previouslyFocused.focus();
            this.previouslyFocused.classList.remove('target-open');
        }

        if (this.lockBodyScroll) {
            unlockBodyScroll(this.modal);
        }

        if (this.hideCallback && typeof this.hideCallback === 'function') {
            this.hideCallback(this.modal);
        }
    };

    minimize = e => {
        this.modal.dataset.modalState = 'minimized';
    };

    maximize = e => {
        this.modal.dataset.modalState = 'maximized';
    };

    adjust = e => {
        if (!this.adjustable) {
            return;
        }

        const ajustmentPosition = this.modal.dataset.modalState;

        if (ajustmentPosition === 'collapsed' || ajustmentPosition === 'minimized') {
            this.maximize();
            return;
        }

        if (ajustmentPosition === 'maximized') {
            this.minimize();
            return;
        }
    };

    autoOpenController() {
        if (this.getModalClosedSession()) return;

        const storedAutoOpenCount = this.getAutoOpenStoredCount() ? parseInt(this.getAutoOpenStoredCount()) : null;

        if (!storedAutoOpenCount) {
            this.show();
            this.setAutoOpenStoredCount(1);

            return;
        }

        if (storedAutoOpenCount < this.autoOpenMaxCount) {
            this.show();
            this.setAutoOpenStoredCount(storedAutoOpenCount + 1);
        }
    }

    getModalControllers() {
        if (!this.adjustable) {
            return this.getModalClosers();
        }

        return [...this.getModalClosers(), ...this.getModalAdjusters()];
    }

    getModalClosers() {
        const innerClosers = [...this.modal.querySelectorAll('[data-modal-closer]')];
        const nextSibling = this.modal.nextElementSibling;
        let adjacentCloser = [];

        if (nextSibling && nextSibling.hasAttribute('data-modal-closer')) {
            adjacentCloser = [nextSibling];
        }

        return [...innerClosers, ...adjacentCloser];
    }

    getModalAdjusters() {
        return [...this.modal.querySelectorAll('[data-modal-minimizer], [data-modal-maximizer]')];
    }

    getAutoOpenStoredCount() {
        return localStorage.getItem(this.localStorageOpenCountKey);
    }

    getModalClosedSession() {
        return sessionStorage.getItem(this.sessionStorageClosedKey);
    }

    setOpenProperties() {
        const peekElements = [...this.modal.querySelectorAll('[data-modal-peek-amount]')];
        const peekElement = peekElements.find(peekElement => getComputedStyle(peekElement)['display'] !== 'none');
        let peekAmount = 0;

        if (peekElement) {
            peekAmount = parseInt(peekElement.dataset.modalPeekAmount) + peekElement.offsetTop - this.modalHeader.offsetHeight - this.modalHeader.offsetTop;
        }

        this.modal.style.setProperty('--maximized-body-height', '100vh');
        this.modal.style.setProperty('--tiled-body-height', '40vh');
        this.modal.style.setProperty('--minimized-body-height', `${peekAmount}px`);
        this.modal.style.setProperty('--collapsed-body-height', 0);
        this.modal.style.setProperty('--modal-max-height', this.maxHeight);
    }

    setClosedProperties() {
        this.modal.dataset.modalState = 'closed';
        this.modalBody.style.setProperty('--modal-body-height', null);
        this.modal.setAttribute('hidden', '');
    }

    setAutoOpenStoredCount(openCount) {
        localStorage.setItem(this.localStorageOpenCountKey, openCount);
    }

    setModalClosedSession() {
        sessionStorage.setItem(this.sessionStorageClosedKey, true);
    }

    hasAnimation(element) {
        return getComputedStyle(element, null)['animation-name'] !== 'none';
    }

    maintainFocus(e) {
        const isInModal = e.target.closest('[aria-modal=true]');

        if (!isInModal) this.moveFocusIn();
    }

    moveFocusIn() {
        const focusTarget = this.modal.querySelector('[autofocus]') || getFocusableChildren(this.modal)[0];

        if (!focusTarget) this.closers[0].focus();
        focusTarget.focus();
    }

    documentBodyClickHandler = e => {
        if (!e.target.closest('[aria-modal=true]') && e.target !== this.previouslyFocused) this.hide();
    };

    addControllerEvents() {
        if (!this.adjustable) {
            this.controllers.forEach(closer => closer.addEventListener('click', this.hide));
            return;
        }

        this.controllers.forEach(controller => {
            if (this.adjustable && controller.closest('.jwc-modal-header')) return;

            if (controller.hasAttribute('data-modal-closer')) {
                controller.addEventListener('click', this.hide);
                return;
            }

            if (controller.hasAttribute('data-modal-minizer')) {
                controller.addEventListener('click', this.adjust);
                return;
            }

            if (controller.hasAttribute('data-modal-maximizer')) {
                controller.addEventListener('click', this.adjust);
                return;
            }
        });
    }

    addPointerEvents() {
        this.pointerActive = false;
        this.currentModalState;
        this.currentBodyHeight;
        this.currentModalBottom;
        this.currentYPos = 0;

        this.modalContent.addEventListener('pointerdown', this.pointerDownHanlder);
    }

    removePointerEvents() {
        this.modalContent.removeEventListener('pointerdown', this.pointerDownHanlder);
        this.modalContent.addEventListener('pointermove', this.pointerMoveHandler);
        this.modalContent.addEventListener('pointerup', this.pointerUpHandler);
        this.modalContent.addEventListener('pointercancel', this.pointerUpHandler);
    }

    pointerDownHanlder = e => {
        this.currentModalState = this.modal.dataset.modalState;
        this.moveFocusIn();

        if (this.currentModalState !== 'collapsed' && this.currentModalState !== 'minimized' && !e.target.closest('.jwc-modal-header')) {
            return;
        }

        if (e.target.closest('[data-modal-closer]')) {
            this.hide(e);
            return;
        }

        if (e.target.closest('[data-modal-minimizer]') || e.target.closest('[data-modal-maximizer]')) {
            this.adjust(e);
            return;
        }

        e.preventDefault();

        this.currentModalState = this.modal.dataset.modalState;
        this.currentBodyHeight = this.modalBody.getBoundingClientRect().height;
        this.currentModalBottom = this.modalContent.getBoundingClientRect().bottom;
        this.currentYPos = e.clientY;
        this.pointerActive = true;

        this.modalContent.setPointerCapture(e.pointerId);
        this.modalContent.addEventListener('pointermove', this.pointerMoveHandler);
        this.modalContent.addEventListener('pointerup', this.pointerUpHandler);
        this.modalContent.addEventListener('pointercancel', this.pointerUpHandler);
    };

    pointerUpHandler = e => {
        if (!this.pointerActive) {
            return;
        }

        const maxHeightNumber = parseInt(this.maxHeight.split('vh'));
        const maxHeightPixels = (window.innerHeight / 100) * maxHeightNumber;
        const pixelsToBottom = this.currentModalBottom - e.clientY;

        this.modalBody.style.height = null;
        this.modalContent.style.transform = null;
        this.modalBody.style.setProperty('--modal-body-height', null);

        if (pixelsToBottom <= 0) {
            this.hide();
        } else if (pixelsToBottom < 100) {
            this.modal.dataset.modalState = 'collapsed';
        } else if (pixelsToBottom < maxHeightPixels * 0.3) {
            this.modal.dataset.modalState = 'minimized';
        } else if (pixelsToBottom < maxHeightPixels * 0.75) {
            this.modal.dataset.modalState = 'tiled';
        } else {
            this.modal.dataset.modalState = 'maximized';
        }

        this.pointerActive = false;
        this.currentBodyHeight = null;
        this.currentModalBottom = null;
        this.currentYPos = 0;

        this.modalContent.removeEventListener('pointermove', this.pointerMoveHandler);
        this.modalContent.removeEventListener('pointerup', this.pointerUpHandler);
        this.modalContent.removeEventListener('pointercancel', this.pointerUpHandler);
    };

    pointerMoveHandler = e => {
        if (!this.pointerActive) {
            return;
        }

        if (e.clientY > this.currentModalBottom) {
            this.hide();
            return;
        }

        let delta = e.clientY - this.currentYPos;
        let adjustedHeight = this.currentBodyHeight - delta > 0 ? this.currentBodyHeight - delta : 0;

        this.modal.dataset.modalState = 'adjusting';
        this.modalBody.style.setProperty('--modal-body-height', `${adjustedHeight}px`);
    };

    keyDownHandler = e => {
        if (e.keyCode === KEYCODE.ESCAPE) {
            this.hide();
        } else if (e.keyCode === KEYCODE.TAB) {
            trapFocus(this.modal, e);
        }
    };
}

function trapFocus(node, e) {
    const focusableChildren = getFocusableChildren(node);
    const focusedItemIndex = focusableChildren.indexOf(document.activeElement);
    const lastIndex = focusableChildren.length - 1;
    const withShift = e.shiftKey;

    if (withShift && focusedItemIndex === 0) {
        focusableChildren[lastIndex].focus();
        e.preventDefault();
    }
    else if (!withShift && focusedItemIndex === lastIndex) {
        focusableChildren[0].focus();
        e.preventDefault();
    }
}

function getFocusableChildren(root) {
    const elements = [...root.querySelectorAll(focusableSelectors.join(','))];

    return elements.filter(isVisible);
}

export const addModal = (target, opener, modalSettings) => {
    const defaultSettings = {
        autoOpen: false,
    }

    const settings =  Object.assign({}, defaultSettings, modalSettings);
    const modal = new Modal(target, opener, settings);

    if (!settings.autoOpen && opener) opener.addEventListener("click", modal.show);

    return modal;
}

export default init = () => {
    modalOpeners = [...document.querySelectorAll('[data-modal-opener], [data-modal-auto-open]')];

    if (modalOpeners.length === 0) return;

    modals = modalOpeners.map(modalOpener => {
        const autoOpener = modalOpener.hasAttribute("data-modal-auto-open");
        const targetId = modalOpener.dataset.target;
        const modal = autoOpener ? modalOpener : document.getElementById(targetId);
        const opener = autoOpener ? null : modalOpener;

        const settings = {
            autoOpen: autoOpener
        }

        if (!modal) {
            console.warn("Modal target not found: ", modalOpener.dataset.target);
            return;
        }

        addModal(modal, opener, settings);
    })
}